/**
 * Betting Zone - Central betting area on table
 *
 * Features:
 * - Circular indicator mesh at table center
 * - Glow effect when bill is nearby
 * - Bill snap positions for placed bets
 * - Stack visualization for multiple bills
 */

import * as THREE from 'three';
import { Bill } from './Bill';
import type { PhysicsWorld } from '../../engine/physics/PhysicsWorld';
import { CashAnims } from '../config/AnimationConfig';
import { sound, SoundId } from '../../engine/audio/sound';

export interface BettingZoneConfig {
  scene: THREE.Scene;
  parent?: THREE.Object3D;
  position?: { x: number; y: number; z: number };
  radius?: number;
  color?: number;
  glowColor?: number;
}

const DEFAULT_POSITION = { x: 0, y: 0, z: 0 };
const DEFAULT_RADIUS = 1.5;
const easeOutBack = (t: number): number => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

export class BettingZone {
  private scene: THREE.Scene;
  private group: THREE.Group;
  private position: THREE.Vector3;
  private radius: number;
  private color: THREE.Color;
  private glowColor: THREE.Color;

  // Zone indicator mesh
  private zoneMesh: THREE.Mesh;
  private glowMesh: THREE.Mesh;

  // Material
  private material: THREE.ShaderMaterial;
  private glowMaterial: THREE.ShaderMaterial;

  // Placed bills
  private placedBills: Bill[] = [];

  // Cancellation callbacks for in-flight drop animations
  private dropAnimationCancellers: Array<() => void> = [];

  // Glow state
  private glowIntensity = 0;
  private targetGlowIntensity = 0;

  // Bet intensity (for pulse effect based on bet amount)
  private betIntensity = 0;
  private targetBetIntensity = 0;

  // Scale feedback for drop zone
  private scale = 1;
  private targetScale = 1;

  constructor(config: BettingZoneConfig) {
    this.scene = config.scene;
    this.radius = config.radius ?? DEFAULT_RADIUS;

    this.position = new THREE.Vector3(
      config.position?.x ?? DEFAULT_POSITION.x,
      config.position?.y ?? DEFAULT_POSITION.y,
      config.position?.z ?? DEFAULT_POSITION.z
    );
    this.color = new THREE.Color(config.color ?? 0xFFD700);
    this.glowColor = new THREE.Color(config.glowColor ?? 0x00FF88);

    this.group = new THREE.Group();
    const parent = config.parent ?? this.scene;
    parent.add(this.group);
    this.group.position.copy(this.position);

    // Create zone indicator
    this.material = this.createZoneMaterial();
    this.glowMaterial = this.createGlowMaterial();

    this.zoneMesh = this.createZoneMesh();
    this.glowMesh = this.createGlowMesh();

    this.group.add(this.zoneMesh);
    this.group.add(this.glowMesh);
  }

  /**
   * Create zone indicator mesh (circle on table)
   */
  private createZoneMesh(): THREE.Mesh {
    const geometry = new THREE.CircleGeometry(this.radius, 64);
    const mesh = new THREE.Mesh(geometry, this.material);

    mesh.rotation.x = -Math.PI / 2; // Lay flat on table
    mesh.position.set(0, 0, 0);
    mesh.receiveShadow = true;
    mesh.userData.sdlBettingUI = true; // Exclude from ground raycasts

    return mesh;
  }

  /**
   * Create glow mesh (slightly larger, pulsing)
   */
  private createGlowMesh(): THREE.Mesh {
    const geometry = new THREE.CircleGeometry(this.radius * 1.2, 64);
    const mesh = new THREE.Mesh(geometry, this.glowMaterial);
    mesh.userData.sdlBettingUI = true; // Exclude from ground raycasts

    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(0, 0.005, 0); // Slightly above zone
    mesh.renderOrder = 1;

    return mesh;
  }

  /**
   * Create zone material with animated border
   */
  private createZoneMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uRadius: { value: this.radius },
        uColor: { value: this.color },
      },
      vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform float uRadius;
        uniform vec3 uColor;

        varying vec2 vUv;

        void main() {
          // Convert UV to center-based coordinates
          vec2 center = vUv - 0.5;
          float dist = length(center) * 2.0;

          // Create ring pattern
          float ring = smoothstep(0.85, 0.87, dist) - smoothstep(0.97, 1.0, dist);

          // Animated dashes
          float angle = atan(center.y, center.x);
          float dashPattern = sin(angle * 10.0 - uTime * 2.0) * 0.5 + 0.5;
          ring *= dashPattern;

          // Inner glow
          float innerGlow = 1.0 - smoothstep(0.0, 0.8, dist);
          innerGlow *= 0.1;

          float alpha = ring + innerGlow;
          vec3 color = uColor;

          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }

  /**
   * Create glow material (pulsing when active)
   */
  private createGlowMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uIntensity: { value: 0 },
        uColor: { value: this.glowColor },
      },
      vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float uTime;
        uniform float uIntensity;
        uniform vec3 uColor;

        varying vec2 vUv;

        void main() {
          vec2 center = vUv - 0.5;
          float dist = length(center) * 2.0;

          // Pulsing glow with speed based on intensity (faster pulse for bigger bets)
          float pulseSpeed = 3.0 + uIntensity * 2.0;
          float pulse = sin(uTime * pulseSpeed) * 0.3 + 0.7;
          float glow = 1.0 - smoothstep(0.0, 1.0, dist);
          glow *= pulse * uIntensity;

          gl_FragColor = vec4(uColor, glow * 0.5);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
  }

  /**
   * Update (call every frame)
   */
  update(deltaTime: number): void {
    // Update time uniforms
    this.material.uniforms.uTime.value += deltaTime;
    this.glowMaterial.uniforms.uTime.value += deltaTime;

    // Animate glow intensity
    const lerpSpeed = 5 * deltaTime;
    this.glowIntensity += (this.targetGlowIntensity - this.glowIntensity) * lerpSpeed;

    // Animate bet intensity (for pulse effect)
    this.betIntensity += (this.targetBetIntensity - this.betIntensity) * 3 * deltaTime;

    // Combine both intensities
    const finalIntensity = Math.max(this.glowIntensity, this.betIntensity);
    this.glowMaterial.uniforms.uIntensity.value = finalIntensity;

    // Snappy scale-up on dotted circle only (origin at zone center)
    this.targetScale = 1 + finalIntensity * 0.08;
    const step = Math.min(1, deltaTime * 12);
    const eased = easeOutBack(step);
    this.scale += (this.targetScale - this.scale) * eased;
    this.zoneMesh.scale.setScalar(this.scale);
  }

  /**
   * Activate glow (when bill is nearby)
   */
  activateGlow(): void {
    this.targetGlowIntensity = 1.0;
  }

  /**
   * Deactivate glow
   */
  deactivateGlow(): void {
    this.targetGlowIntensity = 0;
  }

  /**
   * Check if a point is inside the betting zone
   */
  isPointInZone(point: { x: number; z: number }): boolean {
    const dx = point.x - this.position.x;
    const dz = point.z - this.position.z;
    const distance = Math.sqrt(dx * dx + dz * dz);
    return distance <= this.radius;
  }

  /**
   * Get next snap position for a bill
   * Stacks bills in a neat pile
   */
  getNextSnapPosition(): { x: number; y: number; z: number } {
    const stackHeight = this.placedBills.length * 0.02;

    // Slight offset for visual stacking
    const offsetX = (Math.random() - 0.5) * 0.1;
    const offsetZ = (Math.random() - 0.5) * 0.1;

    return {
      x: this.position.x + offsetX,
      y: this.position.y + stackHeight + 0.01,
      z: this.position.z + offsetZ,
    };
  }

  /**
   * Place a bill in the zone
   */
  placeBill(bill: Bill): void {
    const snapPos = this.getNextSnapPosition();
    bill.animateTo(snapPos, { x: 0, y: 0, z: 0 });
    this.placedBills.push(bill);
  }

  /**
   * Drop bills with animation - fall as pile, scatter on impact
   */
  dropBillsAnimated(
    bills: Bill[],
    onAllSettled: () => void,
    onBillLanded?: () => void,
    muteDropSound = false
  ): void {
    const totalBills = bills.length;

    // Snapshot current row so new bills stack above existing ones
    const baseRow = this.placedBills.length;

    // Track bills immediately so clearBills() can find them even if the
    // zone is removed mid-animation (prevents orphaned bills in scene).
    for (const bill of bills) {
      this.placedBills.push(bill);
    }

    // Cancellation flag — set by clearBills()/reset() to stop the rAF chain
    let cancelled = false;
    const cancel = () => { cancelled = true; };
    this.dropAnimationCancellers.push(cancel);

    console.log(`💰 Dropping ${bills.length} bills - pile drop with scatter (baseRow=${baseRow})`);

    // Pre-calculate landing positions for all bills
    const landingSpots = bills.map((_, index) => {
      const rotZ = (Math.random() - 0.5) * Math.PI * 2; // Random spin -π to +π (both directions)
      // Push direction based on rotation - bills drift in direction they're spinning
      const pushAngle = rotZ + Math.PI / 2; // Perpendicular to rotation
      const pushDistance = CashAnims.zoneScatterPushMin + Math.random() * CashAnims.zoneScatterPushRange;
      const baseAngle = Math.random() * Math.PI * 2;
      const baseDistance = Math.random() * this.radius * 0.5;
      return {
        x: this.position.x + Math.cos(baseAngle) * baseDistance + Math.cos(pushAngle) * pushDistance,
        z: this.position.z + Math.sin(baseAngle) * baseDistance + Math.sin(pushAngle) * pushDistance,
        y: this.position.y + 0.01 + (baseRow + index) * 0.005, // Stack above existing bills
        rotZ,
      };
    });

    // Phase 1: All bills fall together to zone center
    const dropDuration = CashAnims.zoneDropDuration;
    const scatterDuration = CashAnims.zoneScatterDuration;
    const dropStartTime = performance.now();

    // Get starting positions
    const startPositions = bills.map(bill => bill.getPosition().clone());

    // Impact point (zone center, ground level)
    const impactY = this.position.y + 0.01;

    // Store starting rotations (keep from drag)
    const startRotations = bills.map(bill => bill.getRotation().clone());

    const animateDrop = () => {
      if (cancelled) return;
      const elapsed = performance.now() - dropStartTime;
      const t = Math.min(elapsed / dropDuration, 1);

      // Ease-in for accelerating fall (gravity feel)
      const easeIn = t * t;

      // Bend increases exponentially during fall, then snaps flat near ground
      // Starts small, peaks at 80% of fall, then reduces to 0
      let bendAmount: number;
      if (t < 0.8) {
        // Exponential increase during most of fall
        const bendT = t / 0.8;
        bendAmount = Math.pow(bendT, 1.5) * CashAnims.zoneDropBendPeak;
      } else {
        // Quick flatten as approaching ground
        const flattenT = (t - 0.8) / 0.2;
        bendAmount = CashAnims.zoneDropBendPeak * (1 - flattenT);
      }

      bills.forEach((bill, index) => {
        const startPos = startPositions[index];
        const startRot = startRotations[index];

        // All bills converge toward zone center as they fall
        const x = startPos.x + (this.position.x - startPos.x) * easeIn * 0.5;
        const z = startPos.z + (this.position.z - startPos.z) * easeIn * 0.5;
        const y = startPos.y + (impactY - startPos.y) * easeIn;

        bill.setPosition({ x, y, z });
        // Keep original rotation from drag, don't reset
        bill.setRotation({ x: startRot.x, y: startRot.y, z: startRot.z });

        // Apply exponential bend during fall
        bill.setBendImmediate(bendAmount);
      });

      if (t < 1) {
        requestAnimationFrame(animateDrop);
      } else {
        // Phase 2: Impact - scatter outward. Muted for the local player's
        // own create→spawned-zone placement flight (NOTE_MOVE already
        // carried that beat); opponent drops and fades keep the impact.
        if (!muteDropSound) sound.play(SoundId.NOTE_DROP);
        const scatterStartTime = performance.now();
        const impactPositions = bills.map(bill => bill.getPosition().clone());

        // Pre-calculate scatter dynamics per bill
        const scatterDynamics = bills.map(() => ({
          spinAmount: (Math.random() - 0.5) * Math.PI * 2, // Random Z spin during scatter
          bounceHeight: Math.random() * (CashAnims.zoneBounceMax - CashAnims.zoneBounceMin) + CashAnims.zoneBounceMin,
          lateralOffset: {
            x: (Math.random() - 0.5) * 0.3,
            z: (Math.random() - 0.5) * 0.3,
          },
          // Random bend during scatter - most bills bend upward
          scatterBend: Math.random() < 0.8 ? Math.random() * 1.2 + 0.5 : 0.3, // 80% chance 0.5-1.7, else 0.3
        }));

        const animateScatter = () => {
          if (cancelled) return;
          const elapsed = performance.now() - scatterStartTime;
          const t = Math.min(elapsed / scatterDuration, 1);

          // Ease-out for decelerating scatter
          const easeOut = 1 - Math.pow(1 - t, 2);

          // Bounce curve - quick up then down
          const bounce = Math.sin(t * Math.PI) * (1 - t);

          bills.forEach((bill, index) => {
            const impactPos = impactPositions[index];
            const landing = landingSpots[index];
            const dynamics = scatterDynamics[index];

            // Scatter outward with random lateral offset
            const x = impactPos.x + (landing.x - impactPos.x) * easeOut + dynamics.lateralOffset.x * bounce;
            const z = impactPos.z + (landing.z - impactPos.z) * easeOut + dynamics.lateralOffset.z * bounce;
            const y = impactPos.y + (landing.y - impactPos.y) * easeOut + dynamics.bounceHeight * bounce;

            // Z rotation: spin during scatter (bill stays flat via X=-90)
            const rotZ = dynamics.spinAmount * (1 - easeOut) + landing.rotZ * easeOut;

            bill.setPosition({ x, y, z });
            bill.setRotation({ x: -Math.PI / 2, y: 0, z: rotZ }); // Flat (X=-90), spin on Z

            // Random bend during scatter: peak at middle, flatten at end
            // Uses sine curve (peaks mid-animation) for natural look
            // Negative bend to curl UPWARD (opposite direction when flat on table)
            const scatterBendAmount = -dynamics.scatterBend * Math.sin(t * Math.PI);
            bill.setBendImmediate(scatterBendAmount);
          });

          if (t < 1) {
            requestAnimationFrame(animateScatter);
          } else {
            // All settled — finalize positions (bills already in placedBills)
            bills.forEach((bill, index) => {
              const landing = landingSpots[index];
              bill.setPosition({ x: landing.x, y: landing.y, z: landing.z });
              bill.setRotation({ x: -Math.PI / 2, y: 0, z: landing.rotZ }); // Flat (X=-90), random Z spin
              bill.setBendImmediate(0); // Flatten on settle

              if (onBillLanded) {
                onBillLanded();
              }
            });

            console.log(`🎉 All ${totalBills} bills scattered!`);
            onAllSettled();
          }
        };

        requestAnimationFrame(animateScatter);
      }
    };

    requestAnimationFrame(animateDrop);
  }

  /**
   * Drop bills with physics into scattered pile (legacy - use dropBillsAnimated instead)
   */
  dropBillsWithPhysics(
    bills: Bill[],
    _physicsWorld: PhysicsWorld,
    onAllSettled: () => void,
    onBillLanded?: () => void,
    muteDropSound = false
  ): void {
    // Redirect to animated version (physics disabled)
    this.dropBillsAnimated(bills, onAllSettled, onBillLanded, muteDropSound);
  }

  placeBillsImmediate(bills: Bill[]): void {
    bills.forEach((bill) => {
      const snapPos = this.getNextSnapPosition();
      bill.setPosition(snapPos);
      bill.setRotation({ x: -Math.PI / 2, y: 0, z: (Math.random() - 0.5) * Math.PI * 2 });
      bill.setBendImmediate(0);
      this.placedBills.push(bill);
    });
  }

  /**
   * Update bet amount for pulse intensity
   */
  updateBetAmount(totalBet: number, maxBet: number): void {
    // Normalize bet to 0-1 range, then scale to 0.5-1.5 intensity
    const normalized = Math.min(totalBet / maxBet, 1);
    this.targetBetIntensity = 0.5 + normalized * 1.0;
  }

  /**
   * Remove a bill from the zone
   */
  removeBill(bill: Bill): void {
    const index = this.placedBills.indexOf(bill);
    if (index !== -1) {
      this.placedBills.splice(index, 1);
    }
  }

  /**
   * Clear all placed bills and cancel any in-flight drop animations.
   */
  clearBills(): Bill[] {
    for (const cancel of this.dropAnimationCancellers) cancel();
    this.dropAnimationCancellers = [];
    const bills = [...this.placedBills];
    this.placedBills = [];
    return bills;
  }

  /**
   * Get all placed bills
   */
  getPlacedBills(): Bill[] {
    return [...this.placedBills];
  }

  /**
   * Get total bet amount from placed bills
   */
  getTotalBet(): number {
    return this.placedBills.reduce((sum, bill) => sum + bill.getDenomination(), 0);
  }

  /**
   * Get zone position
   */
  getPosition(): THREE.Vector3 {
    return this.position.clone();
  }

  getGroup(): THREE.Group {
    return this.group;
  }

  /**
   * Set zone position
   */
  setPosition(position: { x: number; y: number; z: number }): void {
    this.position.set(position.x, position.y, position.z);
    this.group.position.copy(this.position);
  }

  /**
   * Get zone radius
   */
  getRadius(): number {
    return this.radius;
  }

  /**
   * Show zone
   */
  show(): void {
    this.zoneMesh.visible = true;
    this.glowMesh.visible = true;
  }

  /**
   * Hide zone
   */
  hide(): void {
    this.zoneMesh.visible = false;
    this.glowMesh.visible = false;
  }

  /**
   * Reset zone for pooling reuse.
   * Clears bills, deactivates glow, resets material uniforms, hides zone.
   */
  reset(): void {
    // Cancel any running drop animations
    for (const cancel of this.dropAnimationCancellers) cancel();
    this.dropAnimationCancellers = [];
    // Clear placed bills (caller is responsible for disposing/pooling bills)
    this.placedBills = [];

    // Deactivate glow immediately
    this.glowIntensity = 0;
    this.targetGlowIntensity = 0;
    this.betIntensity = 0;
    this.targetBetIntensity = 0;
    this.glowMaterial.uniforms.uIntensity.value = 0;
    this.scale = 1;
    this.targetScale = 1;
    this.zoneMesh.scale.setScalar(1);

    // Reset time uniforms to avoid visual discontinuity on reuse
    this.material.uniforms.uTime.value = 0;
    this.glowMaterial.uniforms.uTime.value = 0;

    // Hide zone
    this.hide();

    // Detach from parent (will be re-added when reused)
    this.group.parent?.remove(this.group);
  }

  /**
   * Reattach zone to a parent (for pool reuse)
   */
  attachTo(parent: THREE.Object3D): void {
    parent.add(this.group);
  }

  /**
   * Update zone colors (for pool reuse with different zone type)
   */
  setColors(color: number, glowColor: number): void {
    this.color.set(color);
    this.glowColor.set(glowColor);
    this.material.uniforms.uColor.value = this.color;
    this.glowMaterial.uniforms.uColor.value = this.glowColor;
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    // Cancel any running drop animations
    for (const cancel of this.dropAnimationCancellers) cancel();
    this.dropAnimationCancellers = [];

    this.group.parent?.remove(this.group);

    this.zoneMesh.geometry.dispose();
    this.glowMesh.geometry.dispose();
    this.material.dispose();
    this.glowMaterial.dispose();

    this.placedBills = [];
  }
}
